import { buildFiltersFromAidaParams, collectAidaFilterLabelFragments } from './aidaParamToSearchFilter';

const API_ENDPOINT_REGEX = /<api_endpoint>([\s\S]*?)<\/api_endpoint>/gi;

export type ApiEndpointPayload = {
  endpoint: string;
  raw_params?: Record<string, string[]>;
  params?: Record<string, string[]>;
};

export type ApiEndpointSearchLink = {
  label: string;
  pathname: string;
  search: string;
};

function stripIncompleteApiEndpointTag(text: string): string {
  const lastOpen = text.lastIndexOf('<api_endpoint>');
  if (lastOpen === -1) return text;

  const tail = text.slice(lastOpen);
  if (tail.includes('</api_endpoint>')) return text;

  return text.slice(0, lastOpen);
}

function extractDictSection(text: string, key: string): string | null {
  const pattern = new RegExp(`(?:^|[,{]|\\{)\\s*['"]${key}['"]\\s*:\\s*`);
  const match = pattern.exec(text);
  if (!match) return null;

  const start = match.index + match[0].length;
  if (text[start] !== '{') return null;

  let depth = 0;
  for (let i = start; i < text.length; i += 1) {
    if (text[i] === '{') depth += 1;
    else if (text[i] === '}') {
      depth -= 1;
      if (depth === 0) return text.slice(start, i + 1);
    }
  }

  return null;
}

function parseScalarValue(raw: string): string | null {
  const trimmed = raw.trim();
  const quotedMatch = /^['"]((?:\\.|[^'"\\])*)['"]/.exec(trimmed);
  if (quotedMatch) return quotedMatch[1].replace(/\\(['"\\])/g, '$1');
  return null;
}

function parseQuotedStrings(arrayContent: string): string[] {
  const values: string[] = [];
  const valueRegex = /"((?:\\.|[^"\\])*)"|'((?:\\.|[^'\\])*)'/g;
  let match: RegExpExecArray | null;

  while ((match = valueRegex.exec(arrayContent)) !== null) {
    const value = (match[1] ?? match[2]).replace(/\\(['"\\])/g, '$1');
    values.push(value);
  }

  return values;
}

function parseParamDict(dictStr: string): Record<string, string[]> {
  const result: Record<string, string[]> = {};
  const entryRegex = /['"]([^'"]+)['"]\s*:\s*(\[[\s\S]*?\]|['"][^'"]*['"]|-?\d+(?:\.\d+)?)/g;
  let match: RegExpExecArray | null;

  while ((match = entryRegex.exec(dictStr)) !== null) {
    const key = match[1];
    const rawValue = match[2].trim();
    let values: string[] = [];

    if (rawValue.startsWith('[')) {
      values = parseQuotedStrings(rawValue.slice(1, -1));
    } else if (/^-?\d+(?:\.\d+)?$/.test(rawValue)) {
      values = [rawValue];
    } else {
      const scalar = parseScalarValue(rawValue);
      if (scalar) values = [scalar];
    }

    if (values.length > 0) result[key] = values;
  }

  return result;
}

export function parseApiEndpointPayload(raw: string): ApiEndpointPayload | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;

  const endpointMatch = trimmed.match(/['"]endpoint['"]\s*:\s*['"]([^'"]+)['"]/);
  if (!endpointMatch) return null;

  const paramsSection = extractDictSection(trimmed, 'params');
  const rawParamsSection = extractDictSection(trimmed, 'raw_params');

  return {
    endpoint: endpointMatch[1],
    ...(paramsSection ? { params: parseParamDict(paramsSection) } : {}),
    ...(rawParamsSection ? { raw_params: parseParamDict(rawParamsSection) } : {}),
  };
}

function normalizeIdent(paramKey: string, value: string): string {
  if (paramKey === 'disease' || paramKey === 'disease_filter') {
    return value.replace(/^UMLS_/i, '');
  }
  return value;
}

const DISEASE_CUI_REGEX = /^(?:UMLS_)?C\d+$/i;
const GENE_NCBI_ID_REGEX = /^\d+$/;
const VARIANT_IDENT_REGEX = /^(rs\d+|COSM\d+|CM\d+|[0-9]+_[ACGT]+_[ACGT]+_[ACGT]+)$/i;
const CHEMICAL_IDENT_REGEX = /^CHEMBL\d+$/i;

function isDiseaseCui(value: string): boolean {
  return DISEASE_CUI_REGEX.test(value.trim());
}

function isGeneNcbiId(value: string): boolean {
  return GENE_NCBI_ID_REGEX.test(value.trim());
}

function isVariantIdent(value: string): boolean {
  const trimmed = value.trim();
  return VARIANT_IDENT_REGEX.test(trimmed) || (trimmed.length > 0 && !trimmed.includes(' '));
}

function isChemicalIdent(value: string): boolean {
  return CHEMICAL_IDENT_REGEX.test(value.trim());
}

function looksLikeIdent(value: string, paramKey?: string): boolean {
  const trimmed = value.trim();
  if (!trimmed) return false;

  switch (paramKey) {
    case 'disease':
    case 'disease_filter':
      return isDiseaseCui(trimmed);
    case 'gene':
    case 'gene_symbol':
    case 'gene_ncbi_id':
    case 'gene_ncbi_id_filter':
      return isGeneNcbiId(trimmed);
    case 'variant':
    case 'variant_name':
      return isVariantIdent(trimmed);
    case 'chem_ids_init':
    case 'chem_ids_filter':
      return isChemicalIdent(trimmed);
    default:
      return (
        isDiseaseCui(trimmed) ||
        isChemicalIdent(trimmed) ||
        VARIANT_IDENT_REGEX.test(trimmed) ||
        (isGeneNcbiId(trimmed) && Boolean(paramKey?.includes('gene')))
      );
  }
}

type ResolvedIdent = { paramKey: string; value: string };

/**
 * Priority order for entity keys.  Disease is first because DisGeNET is
 * disease-centric: when a payload has both disease and gene, we prefer to
 * generate a disease-based search link so that different payloads covering
 * different diseases produce distinct buttons.
 */
const KEY_PRIORITY = [
  'disease',
  'disease_filter',
  'gene_ncbi_id',
  'gene_ncbi_id_filter',
  'gene',
  'gene_symbol',
  'variant',
  'variant_name',
  'chem_ids_init',
  'chem_ids_filter',
];

/** Returns dict entries in KEY_PRIORITY order, unknown keys appended last. */
function entriesByPriority(dict: Record<string, string[]>): [string, string[]][] {
  const known = KEY_PRIORITY.flatMap<[string, string[]]>(k => (k in dict ? [[k, dict[k]]] : []));
  const rest = Object.entries(dict).filter(([k]) => !KEY_PRIORITY.includes(k));
  return [...known, ...rest];
}

function paramKeyToView(paramKey: string): string | null {
  switch (paramKey) {
    case 'disease':
    case 'disease_filter':
      return 'DISEASES';
    case 'gene':
    case 'gene_symbol':
    case 'gene_ncbi_id':
    case 'gene_ncbi_id_filter':
      return 'GENES';
    case 'variant':
    case 'variant_name':
      return 'VARIANTS';
    case 'chem_ids_init':
    case 'chem_ids_filter':
      return 'CHEMICALS';
    default:
      return null;
  }
}

function expandIdentValue(value: string, paramKey: string): string[] {
  const trimmed = value.trim();
  if (!trimmed) return [];

  if (looksLikeIdent(trimmed, paramKey)) return [trimmed];

  const parts = trimmed
    .split(',')
    .map(part => part.trim())
    .filter(Boolean);
  if (parts.length > 1 && parts.every(part => looksLikeIdent(part, paramKey))) {
    return parts;
  }

  return [];
}

function collectIdentCandidates(dict?: Record<string, string[]>): ResolvedIdent[] {
  if (!dict) return [];

  const resolved: ResolvedIdent[] = [];
  for (const [key, values] of entriesByPriority(dict)) {
    for (const rawValue of values) {
      for (const value of expandIdentValue(String(rawValue), key)) {
        resolved.push({ paramKey: key, value });
      }
    }
  }

  return resolved;
}

/**
 * Fallback: when no canonical ID (CUI, gene NCBI ID, etc.) is found in a dict,
 * return the first non-empty value for a known param key so the search page can
 * resolve it by name via advancedsearch.  Only one candidate is returned to avoid
 * joining multiple names with the '-' ident separator (names can contain hyphens).
 * Uses KEY_PRIORITY order so disease is preferred over gene.
 */
function collectNameFallback(dict?: Record<string, string[]>): ResolvedIdent[] {
  if (!dict) return [];

  for (const [key, values] of entriesByPriority(dict)) {
    if (!paramKeyToView(key)) continue;
    for (const rawValue of values) {
      const value = String(rawValue).trim();
      if (value) return [{ paramKey: key, value }];
    }
  }

  return [];
}

function resolveIdentCandidates(payload: ApiEndpointPayload): ResolvedIdent[] {
  const fromParams = collectIdentCandidates(payload.params);
  if (fromParams.length > 0) return fromParams;

  const fromRawParams = collectIdentCandidates(payload.raw_params);
  if (fromRawParams.length > 0) return fromRawParams;

  const nameFallbackFromParams = collectNameFallback(payload.params);
  if (nameFallbackFromParams.length > 0) return nameFallbackFromParams;

  return collectNameFallback(payload.raw_params);
}

function entityTabFromSegment(segment: string): string | null {
  switch (segment) {
    case 'disease':
      return 'DISEASES';
    case 'gene':
      return 'GENES';
    case 'variant':
      return 'VARIANTS';
    case 'chem':
      return 'CHEMICALS';
    default:
      return null;
  }
}

function associationTabFromSegment(segment: string): string | null {
  switch (segment) {
    case 'gda':
      return 'GDA';
    case 'vda':
      return 'VDA';
    case 'dda':
      return 'DDA';
    default:
      return null;
  }
}

function firstParamIdent(params: Record<string, string[]> | undefined, keys: string[]): string | null {
  if (!params) return null;
  for (const key of keys) {
    const values = params[key];
    if (!values) continue;
    for (const raw of values) {
      const value = String(raw).trim();
      if (value && looksLikeIdent(value, key)) return value;
    }
  }
  return null;
}

function findHumanReadableGeneName(
  rawParams?: Record<string, string[]>,
  params?: Record<string, string[]>,
): string | null {
  for (const key of ['gene', 'gene_symbol']) {
    for (const dict of [rawParams, params]) {
      const values = dict?.[key];
      if (!values) continue;
      const name = values.find(v => {
        const trimmed = v.trim();
        return trimmed && !looksLikeIdent(trimmed, key);
      });
      if (name) return name;
    }
  }

  return null;
}

function describeEndpointKind(endpointPath: string, tab: string): string {
  if (endpointPath.includes('evidences-historical-score')) {
    return `${tab} historical evidence`;
  }

  if (endpointPath.includes('/evidence') || endpointPath.endsWith('evidence')) {
    return `${tab} evidence`;
  }

  if (endpointPath.includes('/summary') || endpointPath.endsWith('summary')) {
    return `${tab} summary`;
  }

  if (endpointPath.includes('/all') || endpointPath.endsWith('all')) {
    switch (tab) {
      case 'DISEASES':
        return 'disease profile';
      case 'GENES':
        return 'gene profile';
      case 'VARIANTS':
        return 'variant profile';
      case 'CHEMICALS':
        return 'chemical profile';
      default:
        return `${tab.toLowerCase()} profile`;
    }
  }

  switch (tab) {
    case 'GDA':
      return 'gene-disease associations';
    case 'VDA':
      return 'variant-disease associations';
    case 'DDA':
      return 'disease-drug associations';
    case 'DISEASES':
      return 'disease profile';
    case 'GENES':
      return 'gene profile';
    case 'VARIANTS':
      return 'variant profile';
    case 'CHEMICALS':
      return 'chemical profile';
    default:
      return 'DISGENET search results';
  }
}

function buildLabelQualifiers(
  endpointPath: string,
  rawParams: Record<string, string[]> | undefined,
  params: Record<string, string[]> | undefined,
  primaryParamKey?: string,
): string {
  const qualifiers: string[] = [];

  const yearMax = params?.historical_score_year_max?.[0] ?? rawParams?.historical_score_year_max?.[0];
  if (yearMax && /^\d+$/.test(yearMax)) {
    qualifiers.push(`until ${yearMax}`);
  }

  const hasGeneFilter = firstParamIdent(params, ['gene_ncbi_id', 'gene_ncbi_id_filter']);
  const primaryIsGene = primaryParamKey ? paramKeyToView(primaryParamKey) === 'GENES' : false;
  if (hasGeneFilter && !primaryIsGene) {
    const geneName = findHumanReadableGeneName(rawParams, params);
    qualifiers.push(geneName ? `gene ${geneName}` : `gene ID ${hasGeneFilter}`);
  }

  qualifiers.push(...collectAidaFilterLabelFragments(params, rawParams));

  const orderBy = params?.order_by?.[0] ?? rawParams?.order_by?.[0];
  if (orderBy && endpointPath.includes('summary')) {
    qualifiers.push(`sorted by ${orderBy}`);
  }

  return qualifiers.length > 0 ? ` (${qualifiers.join(', ')})` : '';
}

function buildLinkLabel(
  endpointPath: string,
  tab: string,
  rawParams: Record<string, string[]> | undefined,
  params: Record<string, string[]> | undefined,
  identCandidates: ResolvedIdent[] = [],
): string {
  const primaryIdent = identCandidates[0];
  const primaryView = primaryIdent ? paramKeyToView(primaryIdent.paramKey) : null;
  const sameTypeCount = primaryView
    ? identCandidates.filter(c => paramKeyToView(c.paramKey) === primaryView).length
    : 0;

  const entityName = (() => {
    if (!primaryIdent) return null;
    const key = primaryIdent.paramKey;

    if (paramKeyToView(key) === 'GENES') {
      return findHumanReadableGeneName(rawParams, params) ?? normalizeIdent(key, primaryIdent.value);
    }

    const nameFromDict = (dict?: Record<string, string[]>): string | null => {
      const values = dict?.[key];
      if (!values) return null;
      return (
        values.find(v => {
          const trimmed = v.trim();
          return trimmed && !looksLikeIdent(trimmed, key);
        }) ?? null
      );
    };

    return nameFromDict(rawParams) ?? nameFromDict(params) ?? normalizeIdent(key, primaryIdent.value);
  })();

  const entityLabel = (() => {
    if (!entityName) return sameTypeCount > 1 ? `${sameTypeCount} results` : 'results';
    if (sameTypeCount > 1) return `${entityName} and ${sameTypeCount - 1} more`;
    return entityName;
  })();

  const endpointKind = describeEndpointKind(endpointPath, tab);
  const qualifiers = buildLabelQualifiers(endpointPath, rawParams, params, primaryIdent?.paramKey);

  return `View ${endpointKind} for ${entityLabel}${qualifiers}`;
}

function buildExtraFilterParams(
  params: Record<string, string[]> | undefined,
  tab: string,
  view: string,
  isEvidence: boolean,
): URLSearchParams {
  return buildFiltersFromAidaParams({ params, tab, view, isEvidence });
}

export function buildSearchLinkFromApiEndpoint(
  payload: ApiEndpointPayload,
  options: { source?: string } = {},
): ApiEndpointSearchLink | null {
  const source = options.source ?? 'ALL';
  const endpointPath = payload.endpoint.replace(/^\/api\/v1\//, '').replace(/^\//, '');

  const isEvidence = endpointPath.includes('evidence');

  const segments = endpointPath.split('/');
  const associationTab = segments.reduce<string | null>(
    (acc, seg) => acc ?? associationTabFromSegment(seg),
    null,
  );
  const entityTab = segments.reduce<string | null>((acc, seg) => acc ?? entityTabFromSegment(seg), null);

  const identCandidates = resolveIdentCandidates(payload);
  if (identCandidates.length === 0) return null;

  const primaryIdent = identCandidates[0];
  const idents = identCandidates
    .map(candidate => normalizeIdent(candidate.paramKey, candidate.value))
    .join('-');

  const view = paramKeyToView(primaryIdent.paramKey) ?? entityTab;
  if (!view) return null;

  const tab = associationTab ?? entityTab ?? view;

  const searchParams = new URLSearchParams({ view, idents, source, tab });
  const extraFilters = buildExtraFilterParams(payload.params, tab, view, isEvidence);
  for (const [key, value] of extraFilters.entries()) {
    searchParams.set(key, value);
  }

  return {
    label: buildLinkLabel(endpointPath, tab, payload.raw_params, payload.params, identCandidates),
    pathname: '/search',
    search: `?${searchParams.toString()}`,
  };
}

export function stripApiEndpointTags(text: string): string {
  const withoutCompleteTags = text.replace(new RegExp(API_ENDPOINT_REGEX.source, 'gi'), '');
  return stripIncompleteApiEndpointTag(withoutCompleteTags).trimEnd();
}

function splitApiEndpointPayloads(tagContent: string): string[] {
  return tagContent.split(/\s*(?:\||,)\s*(?=\{)/);
}

function deduplicateSearchLinks(links: ApiEndpointSearchLink[]): ApiEndpointSearchLink[] {
  const seen = new Set<string>();

  return links.filter(link => {
    const key = `${link.pathname}${link.search}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function normalizeApiEndpointMarkup(text: string): string {
  return text
    .replace(/&lt;api_endpoint&gt;/gi, '<api_endpoint>')
    .replace(/&lt;\/api_endpoint&gt;/gi, '</api_endpoint>');
}

export function parseApiEndpointLinks(
  text: string,
  options: { source?: string } = {},
): { cleanedText: string; links: ApiEndpointSearchLink[] } {
  const normalizedText = normalizeApiEndpointMarkup(text);
  const links: ApiEndpointSearchLink[] = [];
  const regex = new RegExp(API_ENDPOINT_REGEX.source, 'gi');
  let match: RegExpExecArray | null;

  while ((match = regex.exec(normalizedText)) !== null) {
    const payloadStrings = splitApiEndpointPayloads(match[1]);

    for (const payloadStr of payloadStrings) {
      const payload = parseApiEndpointPayload(payloadStr.trim());
      if (!payload) continue;

      const link = buildSearchLinkFromApiEndpoint(payload, options);
      if (link) links.push(link);
    }
  }

  return {
    cleanedText: stripApiEndpointTags(normalizedText),
    links: deduplicateSearchLinks(links),
  };
}
